import { useEffect } from 'react';
import Header from './components/Header';
import ABTestHero from './components/ABTestHero';
import StatsBanner from './components/StatsBanner';
import CarriersStrip from './components/CarriersStrip';
import Services from './components/Services';
import CredibilityStats from './components/CredibilityStats';
import WhyLocal from './components/WhyLocal';
import MeetYourAgent from './components/MeetYourAgent';
import SocialProof from './components/SocialProof';
import HomePricingSection from './components/HomePricingSection';
import LocalCoverageSection from './components/LocalCoverageSection';
import CitiesWeServeHub from './components/CitiesWeServeHub';
import SpecialtyCoverageLinks from './components/SpecialtyCoverageLinks';
import Educational from './components/Educational';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Footer from './components/Footer';
import MobileFloatingCTA from './components/MobileFloatingCTA';
import DesktopFloatingCTA from './components/DesktopFloatingCTA';
import ExitIntentPopup from '../../components/feature/ExitIntentPopup';
import UTMBanner from '../../components/feature/UTMBanner';
import NeedsAssessmentPopup from './components/NeedsAssessmentPopup';
import MobileStickyTopBar from '../../components/feature/MobileStickyTopBar';
import { useUTMTracking } from '../../hooks/useUTMTracking';
import { useAnalytics } from '../../hooks/useAnalytics';

export default function HomePage() {
  useUTMTracking();
  useAnalytics();

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

    document.title = 'Hoosier Life Insurance | Indianapolis Coverage from $18/mo';

    // Meta description — must be ≤160 chars
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Hoosier Life Insurance — Indiana\'s independent agency serving individuals, families & seniors statewide. Term life, whole life, IUL, final expense & annuities from 20+ A-rated carriers. Free quotes (317) 648-2983.');

    // Meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'life insurance Indiana, Indiana life insurance agent, affordable life insurance quotes, term life insurance Indiana, whole life insurance Indiana, final expense insurance Indiana, IUL indexed universal life, annuities Indiana, mortgage protection Indiana');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', siteUrl);

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Hoosier Life Insurance | Independent Agency — Indianapolis, Indiana' },
      { property: 'og:description', content: 'Hoosier Life Insurance is Indiana\'s independent agency serving individuals, families & seniors statewide. Compare 20+ A-rated carriers. Free quotes — (317) 648-2983.' },
      { property: 'og:url', content: siteUrl },
      { property: 'og:type', content: 'website' },
      { property: 'og:locale', content: 'en_US' },
    ];
    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Hoosier Life Insurance | Indiana Life Insurance Specialists' },
      { name: 'twitter:description', content: 'Indiana\'s independent life insurance team. Compare 20+ A-rated carriers. Free quotes.' },
    ];
    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // --- Schema Markup ---

    // 1. Organization Schema
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'Hoosier Life Insurance',
      url: siteUrl,
      logo: `${siteUrl}/og-image.jpg`,
      slogan: 'Indiana\'s Independent Life Insurance Agency — Serving Individuals, Families & Seniors Statewide',
      description: 'Hoosier Life Insurance is Indiana\'s independent life insurance agency serving individuals, families, and seniors across all 92 Indiana counties. Licensed specialists compare 20+ top-rated carriers for term life, whole life, final expense insurance, indexed universal life (IUL), and annuities. Based in Indianapolis with phone and virtual consultations statewide.',
      telephone: '(317) 648-2983',
      email: 'info@hoosierlifeinsurance.com',
      foundingDate: '2018',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '5625 German Church Road',
        addressLocality: 'Indianapolis',
        addressRegion: 'IN',
        postalCode: '46235',
        addressCountry: 'US',
      },
      areaServed: [
        { '@type': 'State', name: 'Indiana', '@id': 'https://en.wikipedia.org/wiki/Indiana' },
        { '@type': 'City', name: 'Indianapolis', '@id': 'https://en.wikipedia.org/wiki/Indianapolis' },
      ],
      numberOfEmployees: { '@type': 'QuantitativeValue', value: 5 },
      knowsAbout: [
        'Term Life Insurance',
        'Whole Life Insurance',
        'Indexed Universal Life Insurance',
        'Final Expense Insurance',
        'Burial Insurance',
        'Mortgage Protection Insurance',
        'Annuities',
        'No Medical Exam Life Insurance',
        'Life Insurance for Seniors',
        'Life Insurance for Diabetics',
        'Life Insurance for High-Risk Applicants',
        'Life Insurance for Self-Employed',
        'Group Life Insurance',
        'Business Life Insurance',
      ],
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '(317) 648-2983',
          contactType: 'customer service',
          areaServed: 'IN',
          availableLanguage: 'English',
          hoursAvailable: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '18:00',
            },
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Saturday'],
              opens: '09:00',
              closes: '13:00',
            },
          ],
        },
        {
          '@type': 'ContactPoint',
          telephone: '(317) 648-2983',
          contactType: 'sales',
          areaServed: 'IN',
          availableLanguage: 'English',
        },
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '127',
        bestRating: '5',
        worstRating: '1',
      },
      sameAs: [
        'https://www.facebook.com/hoosierlifeinsurance',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
    };

    // 2. InsuranceAgency / LocalBusiness Schema  — @id uses #business per AI audit recommendation
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': ['InsuranceAgency', 'FinancialService', 'LocalBusiness'],
      '@id': `${siteUrl}/#business`,
      name: 'Hoosier Life Insurance',
      alternateName: 'Hoosier Life Insurance Agency',
      url: siteUrl,
      telephone: '(317) 648-2983',
      email: 'info@hoosierlifeinsurance.com',
      foundingDate: '2018',
      description: 'Hoosier Life Insurance is an independent life insurance agency based in Indianapolis, Indiana, serving individuals, families, and seniors across all 92 Indiana counties. We compare 20+ A-rated carriers for term life, whole life, IUL, final expense, and annuity products.',
      priceRange: '$$',
      currenciesAccepted: 'USD',
      paymentAccepted: 'Cash, Check, Credit Card, ACH',
      numberOfEmployees: { '@type': 'QuantitativeValue', value: 5 },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '5625 German Church Road',
        addressLocality: 'Indianapolis',
        addressRegion: 'IN',
        postalCode: '46235',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 39.8012,
        longitude: -86.0139,
      },
      hasMap: 'https://maps.google.com/?q=5625+German+Church+Road+Indianapolis+IN+46235',
      image: [
        `${siteUrl}/og-image.jpg`,
      ],
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/og-image.jpg`,
        width: 1200,
        height: 630,
      },
      parentOrganization: { '@id': `${siteUrl}/#organization` },
      areaServed: [
        { '@type': 'State', name: 'Indiana', '@id': 'https://en.wikipedia.org/wiki/Indiana' },
        { '@type': 'City', name: 'Indianapolis', '@id': 'https://en.wikipedia.org/wiki/Indianapolis' },
        { '@type': 'City', name: 'Carmel' },
        { '@type': 'City', name: 'Fishers' },
        { '@type': 'City', name: 'Noblesville' },
        { '@type': 'City', name: 'Zionsville' },
        { '@type': 'City', name: 'Westfield' },
        { '@type': 'City', name: 'Fort Wayne' },
        { '@type': 'City', name: 'Evansville' },
        { '@type': 'City', name: 'South Bend' },
      ],
      knowsAbout: [
        'Term Life Insurance',
        'Whole Life Insurance',
        'Indexed Universal Life Insurance',
        'Final Expense Insurance',
        'Burial Insurance',
        'Mortgage Protection Insurance',
        'Annuities',
        'No Medical Exam Life Insurance',
        'Life Insurance for Seniors',
        'Life Insurance for Diabetics',
        'Life Insurance for High-Risk Applicants',
        'Life Insurance for Self-Employed',
        'Group Life Insurance',
        'Business Life Insurance',
      ],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '127',
        bestRating: '5',
        worstRating: '1',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday'],
          opens: '09:00',
          closes: '13:00',
        },
      ],
      sameAs: [
        'https://www.facebook.com/hoosierlifeinsurance',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '(317) 648-2983',
          contactType: 'customer service',
          areaServed: 'IN',
          availableLanguage: 'English',
          hoursAvailable: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '18:00',
            },
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Saturday'],
              opens: '09:00',
              closes: '13:00',
            },
          ],
        },
        {
          '@type': 'ContactPoint',
          telephone: '(317) 648-2983',
          contactType: 'sales',
          areaServed: 'IN',
          availableLanguage: 'English',
        },
      ],
    };

    // 3. WebSite Schema
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Hoosier Life Insurance',
      description: 'Indiana\'s independent life insurance team — phone and virtual consultations serving all 92 Indiana counties',
      publisher: { '@id': `${siteUrl}/#organization` },
      potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${siteUrl}/instant-quote?q={search_term_string}` },
        'query-input': 'required name=search_term_string',
      },
    };

    // 4. FAQPage Schema — top 5 verbatim Q&As injected as JSON-LD
    // This is the primary signal Google Featured Snippets and AI systems (ChatGPT, Perplexity, Gemini)
    // pull from when answering questions about Indiana life insurance pricing and coverage.
    const faqPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/#faq-homepage`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is term life insurance and how does it work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Term life insurance provides a death benefit to your beneficiaries if you pass away during a set coverage period — typically 10, 20, or 30 years. You pay a fixed monthly premium, and if you die within the term, your family receives a tax-free lump sum. If you outlive the term, the policy simply expires. It\'s the most straightforward, affordable way to protect your family during your highest-earning, highest-responsibility years.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much does term life insurance cost in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Term life is far more affordable than most people expect. A healthy 35-year-old in Indiana can get $500,000 of 20-year term coverage for as little as $25–$35/month — that\'s less than a dollar a day. Rates depend on your age, health, coverage amount, and term length. A healthy 30-year-old pays around $20–$28/month for $500,000 of coverage. The younger and healthier you are when you apply, the lower your rate will be — and it stays locked in for the entire term.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much does life insurance cost per month in Indianapolis?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Life insurance costs vary by age, health, and coverage amount. A healthy 30-year-old in Indianapolis can get $500,000 of 20-year term life insurance for around $20–$28/month. A healthy 35-year-old pays approximately $28–$35/month for the same coverage. A 45-year-old in good health might pay $60–$90/month. Whole life insurance costs 5–15x more than term for the same death benefit. Get a free personalized quote from Hoosier Life Insurance at (317) 648-2983.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between term life and whole life insurance?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Term life insurance provides coverage for a specific period (10, 20, or 30 years) at a much lower cost — ideal for families who need maximum protection during their working years. Whole life insurance lasts your entire lifetime and builds cash value over time, but costs significantly more. For most Indianapolis families, term life offers the best value. Hoosier Life Insurance compares both so you choose what truly fits your budget and goals.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much life insurance do I need in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A common rule of thumb is 10–12 times your annual income. For example, if you earn $60,000/year, you should consider $600,000–$720,000 in coverage. Indiana\'s median household income is $63,800, meaning most Indiana families need $640,000–$766,000 in coverage. But the right amount depends on your mortgage balance, number of dependents, debts, and future education costs. Hoosier Life Insurance offers a free Financial Needs Analysis to give you a precise, personalized number.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I get life insurance without a medical exam in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Many carriers offer simplified or no-exam life insurance policies in Indiana, especially for younger, healthier applicants or smaller coverage amounts. No medical exam life insurance can often be approved the same day or next day. Hoosier Life Insurance works with multiple carriers to find the best no-exam option for your health profile and coverage needs.',
          },
        },
        {
          '@type': 'Question',
          name: 'What does it cost to work with Hoosier Life Insurance?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Our services are completely free to you. Hoosier Life Insurance is compensated by the insurance carriers when you purchase a policy — at no extra cost to you. The price you pay for a policy is the same whether you buy directly from the carrier or through us. You get expert guidance, comparison shopping across 20+ A-rated carriers, and ongoing support at zero additional cost. Free quotes are available by calling (317) 648-2983.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I get life insurance if I have diabetes or high blood pressure?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Many Indiana residents with Type 2 diabetes, hypertension, or other pre-existing conditions can still qualify for affordable life insurance. Hoosier Life Insurance works with multiple carriers that specialize in high-risk underwriting to find you the best rates for your specific health situation.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I get a free life insurance quote in Indianapolis?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You can get a free life insurance quote from Hoosier Life Insurance in minutes by visiting hoosierlifeinsurance.com/instant-quote or calling (317) 648-2983. We compare 20+ A-rated carriers — no pressure, no obligation.',
          },
        },
      ],
    };

    // 5. SpeakableSpecification Schema — expanded to target FAQ section
    // cssSelector targets both section-level and individual answer elements for maximum AI extraction coverage.
    // AI voice assistants and search engines (Google SGE, ChatGPT, Perplexity) use these selectors to
    // identify the most relevant, speakable content blocks on the page.
    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/#speakable`,
      url: siteUrl,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: [
          // Hero + headline
          'h1',
          // FAQ section — entire block (highest priority for Q&A extraction)
          '#faq',
          '#faq h2',
          // Top 7 individual FAQ answers by ID (surgically targeted answer text)
          '#faq-answer-text-0',
          '#faq-answer-text-1',
          '#faq-answer-text-2',
          '#faq-answer-text-3',
          '#faq-answer-text-4',
          '#faq-answer-text-5',
          '#faq-answer-text-6',
          // Pricing + stats sections
          '.stats-banner',
          '#pricing-summary',
          '#fishers-price-summary',
          '#noblesville-price-summary',
        ],
      },
    };

    // 6. WebPage Schema — now includes speakable property inline for belt-and-suspenders coverage
    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      url: siteUrl,
      name: 'Hoosier Life Insurance | Indianapolis Coverage from $18/mo',
      description: 'Indiana\'s independent life insurance agency. Compare 20+ A-rated carriers for term life, whole life, IUL, final expense & annuities. Free quotes — (317) 648-2983.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0] + 'T00:00:00-05:00',
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['#faq', '#faq-answer-text-0', '#faq-answer-text-1', '#faq-answer-text-2'],
      },
    };

    const schemas = [
      organizationSchema,
      localBusinessSchema,
      websiteSchema,
      faqPageSchema,
      speakableSchema,
      webPageSchema,
    ];

    const scriptIds = [
      'schema-organization',
      'schema-localbusiness',
      'schema-website',
      'schema-faqpage-homepage',
      'schema-speakable',
      'schema-webpage',
    ];

    schemas.forEach((schema, index) => {
      let script = document.getElementById(scriptIds[index]) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = scriptIds[index];
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    });

    return () => {
      scriptIds.forEach(id => {
        const script = document.getElementById(id);
        if (script) script.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Skip navigation link — keyboard/screen reader accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-[#1A3A52] focus:text-white focus:text-sm focus:font-semibold focus:rounded-lg focus:outline-none"
      >
        Skip to main content
      </a>
      <MobileStickyTopBar quoteHref="#quote" />
      <UTMBanner />
      <Header />
      <main id="main-content">
        <ABTestHero />
        <StatsBanner />
        <CarriersStrip />
        <Services />
        <CredibilityStats />
        <WhyLocal />
        <MeetYourAgent />
        <SocialProof />
        <HomePricingSection />
        <LocalCoverageSection />
        <CitiesWeServeHub />
        <Educational />
        <FAQ />
        <Contact />
        <SpecialtyCoverageLinks />
      </main>
      <Footer />
      <MobileFloatingCTA />
      <DesktopFloatingCTA />
      <ExitIntentPopup />
      <NeedsAssessmentPopup />
    </div>
  );
}